/**
 * DaddyTalkSettings — Admin / HM page at /settings/daddytalk to control
 * the workspace-level DaddyTalk toggle and per-recruiter visibility.
 *
 * Ties to the M1 permission model. No new backend endpoints — just
 * pretty UI on top of GET/PUT workspace-settings, GET recruiters,
 * PUT recruiters/{id}/visibility.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Toggle,
    ShieldCheck,
    Clock,
    Users,
    ChatCircleDots,
    Check,
} from "@phosphor-icons/react";

export default function DaddyTalkSettings() {
    const [ws, setWs] = useState(null);
    const [recs, setRecs] = useState([]);
    const [presence, setPresence] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingRow, setSavingRow] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [ws1, rec1, pres1] = await Promise.all([
                api.get("/daddytalk/workspace-settings"),
                api.get("/daddytalk/recruiters"),
                api.get("/daddytalk/presence-analytics?days=7").catch(() => ({ data: null })),
            ]);
            setWs(ws1.data);
            setRecs(rec1.data || []);
            setPresence(pres1.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load DaddyTalk settings");
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); }, []);

    async function updateWs(patch) {
        try {
            const { data } = await api.put("/daddytalk/workspace-settings", patch);
            setWs(data);
            toast.success("Workspace settings updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update");
        }
    }

    async function toggleVisibility(id, next) {
        setSavingRow(id);
        try {
            await api.put(`/daddytalk/recruiters/${id}/visibility`, { chat_visible: next });
            await load();
            toast.success(next ? "Recruiter now visible in DaddyTalk" : "Recruiter hidden from DaddyTalk");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update visibility");
        } finally { setSavingRow(null); }
    }

    async function updateCapacity(id, cap) {
        try {
            await api.put(`/daddytalk/recruiters/${id}/visibility`, {
                chat_visible: recs.find((r) => r.user_id === id)?.chat_visible ?? false,
                max_concurrent_chats: cap,
            });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update capacity");
        }
    }

    // V1.6.5 — Bulk flip visibility for the whole workspace at once.
    // Backend skips synthetic test-user accounts by default so a fresh
    // demo doesn't publish spam profiles into the candidate directory.
    async function bulkVisibility(next) {
        const label = next ? "enable" : "hide";
        if (!window.confirm(`${next ? "Enable" : "Hide"} ALL real recruiters in DaddyTalk? (Test-user accounts will be skipped.)`)) return;
        try {
            const { data } = await api.put("/daddytalk/recruiters/bulk-visibility", { chat_visible: next });
            await load();
            toast.success(`${data.updated} recruiter${data.updated === 1 ? "" : "s"} ${label}d${data.skipped_test_users?.length ? ` · skipped ${data.skipped_test_users.length} test account${data.skipped_test_users.length === 1 ? "" : "s"}` : ""}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || `Failed to ${label} recruiters`);
        }
    }

    if (loading) {
        return <div className="p-6 text-sm text-ink/50">Loading DaddyTalk settings…</div>;
    }

    return (
        <div className="p-6 space-y-8 max-w-5xl" data-testid="daddytalk-settings-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <img
                        src="/mascot/daddytalk/avatar_hello.png"
                        alt=""
                        className="w-12 h-12 rounded-full border-2 border-cream"
                    />
                    <div>
                        <h1 className="font-heading text-2xl font-black">DaddyTalk Settings</h1>
                        <p className="text-xs text-ink/50 mt-1">
                            Control who's visible in the candidate-facing recruiter directory.
                        </p>
                    </div>
                </div>
            </div>

            {/* Workspace policy */}
            <section className="bg-white border-2 border-ink/10" data-testid="daddytalk-workspace-policy">
                <header className="px-5 py-3 border-b border-ink/10 bg-cream/50 flex items-center gap-2">
                    <ShieldCheck size={14} weight="bold" className="text-cobalt" />
                    <h2 className="font-heading text-sm font-black uppercase tracking-wider">
                        Workspace policy
                    </h2>
                </header>
                <div className="p-5 space-y-4">
                    <ToggleRow
                        testid="daddytalk-enabled-toggle"
                        label="DaddyTalk enabled for this workspace"
                        hint="Master switch. When off, no candidate can start a chat with any recruiter here."
                        value={!!ws?.daddytalk_enabled}
                        onChange={(v) => updateWs({ daddytalk_enabled: v })}
                    />
                    <ToggleRow
                        testid="daddytalk-accepting-toggle"
                        label="Currently accepting chats"
                        hint="Toggle off for holidays / off-hours. Existing chats stay open."
                        value={!!ws?.chat_accepting}
                        onChange={(v) => updateWs({ chat_accepting: v })}
                        disabled={!ws?.daddytalk_enabled}
                    />
                    <ToggleRow
                        testid="daddytalk-default-visible-toggle"
                        label="Auto-enable new hires by default"
                        hint="When you invite a new recruiter, should they appear in DaddyTalk immediately?"
                        value={!!ws?.default_visibility_for_new}
                        onChange={(v) => updateWs({ default_visibility_for_new: v })}
                        disabled={!ws?.daddytalk_enabled}
                    />
                    <ToggleRow
                        testid="daddytalk-ai-autoresponder-toggle"
                        label="Uncle D AI first-line autoresponder"
                        hint="If a recruiter is offline, Uncle D drafts a warm first reply within 30s so cold candidates don't feel ghosted. Never promises a job or interview."
                        value={!!ws?.daddytalk_ai_autoresponder}
                        onChange={(v) => updateWs({ daddytalk_ai_autoresponder: v })}
                        disabled={!ws?.daddytalk_enabled}
                    />
                    <div className="pt-2 border-t border-ink/5 flex items-center gap-3">
                        <Clock size={14} weight="bold" className="text-ink/40" />
                        <label className="flex items-center gap-2 text-sm">
                            <span className="text-ink/70">Response SLA target:</span>
                            <input
                                type="number"
                                min={1}
                                max={168}
                                value={ws?.sla_hours ?? 24}
                                onChange={(e) => setWs({ ...ws, sla_hours: Number(e.target.value) })}
                                onBlur={(e) => updateWs({ sla_hours: Number(e.target.value) })}
                                data-testid="daddytalk-sla-input"
                                className="w-20 px-2 py-1 border border-ink/20 text-sm focus:outline-none focus:border-cobalt"
                            />
                            <span className="text-ink/50">hours</span>
                        </label>
                    </div>
                </div>
            </section>

            {/* Recruiter visibility matrix */}
            <section className="bg-white border-2 border-ink/10" data-testid="daddytalk-recruiter-matrix">
                <header className="px-5 py-3 border-b border-ink/10 bg-cream/50 flex items-center gap-2 flex-wrap">
                    <Users size={14} weight="bold" className="text-cobalt" />
                    <h2 className="font-heading text-sm font-black uppercase tracking-wider">
                        Recruiter visibility ({recs.length})
                    </h2>
                    {/* V1.6.5 — Bulk toggles so admins don't click 12 individual switches. */}
                    <button
                        type="button"
                        data-testid="daddytalk-bulk-enable"
                        onClick={() => bulkVisibility(true)}
                        className="ml-3 text-[10px] font-black uppercase tracking-wider bg-cobalt text-white px-3 py-1.5 hover:bg-cobalt-dark transition-colors"
                    >
                        Enable all
                    </button>
                    <button
                        type="button"
                        data-testid="daddytalk-bulk-hide"
                        onClick={() => bulkVisibility(false)}
                        className="text-[10px] font-black uppercase tracking-wider border-2 border-ink text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
                    >
                        Hide all
                    </button>
                    <span className="ml-auto text-[10px] text-ink/40">
                        Only recruiters marked <strong>Visible</strong> + set to <strong>Available</strong> appear in the candidate directory. Bulk toggles skip test-user accounts.
                    </span>
                </header>
                {recs.length === 0 ? (
                    <div className="p-8 text-center text-sm text-ink/50">
                        No recruiters found in this workspace. Invite one from the Team page.
                    </div>
                ) : (
                    <>
                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y divide-ink/5">
                        {recs.map((r) => (
                            <div key={`m-${r.user_id}`} data-testid={`daddytalk-rec-card-${r.user_id}`} className="p-4 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="font-bold truncate">{r.name || "Unnamed"}</p>
                                        <p className="text-[10px] text-ink/40 truncate">{r.email}</p>
                                        <p className="text-[10px] text-ink/60 uppercase mt-0.5">
                                            {r.role === "hiring_manager" ? "Hiring Mgr" : r.role}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0 space-y-1">
                                        <span className={`block text-[10px] font-black uppercase tracking-wider ${
                                            r.chat_active === "available" ? "text-emerald-600" :
                                            r.chat_active === "away" ? "text-amber-600" : "text-red-600"
                                        }`}>
                                            {r.chat_active}
                                        </span>
                                        {r.eligible ? (
                                            <span data-testid={`daddytalk-eligible-yes-card-${r.user_id}`}
                                                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-600">
                                                <Check size={11} weight="bold" /> Live
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-black uppercase tracking-wider text-ink/30">Hidden</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 pt-2 border-t border-ink/5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-wider text-ink/50">Visible</span>
                                        <button
                                            type="button"
                                            onClick={() => toggleVisibility(r.user_id, !r.chat_visible)}
                                            disabled={savingRow === r.user_id}
                                            data-testid={`daddytalk-visibility-toggle-card-${r.user_id}`}
                                            className={`relative inline-flex h-5 w-9 items-center transition-colors ${
                                                r.chat_visible ? "bg-cobalt" : "bg-ink/20"
                                            }`}
                                        >
                                            <span className={`inline-block h-4 w-4 transform bg-white transition-transform ${
                                                r.chat_visible ? "translate-x-4" : "translate-x-0.5"
                                            }`} />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-wider text-ink/50">Capacity</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={200}
                                            defaultValue={r.max_concurrent_chats || 20}
                                            onBlur={(e) => {
                                                const v = Number(e.target.value);
                                                if (v !== r.max_concurrent_chats) updateCapacity(r.user_id, v);
                                            }}
                                            data-testid={`daddytalk-capacity-card-${r.user_id}`}
                                            className="w-14 px-2 py-1 border border-ink/20 text-xs focus:outline-none focus:border-cobalt"
                                        />
                                    </div>
                                    <div className="ml-auto text-xs text-ink/60">
                                        {r.current_open_chats || 0} / {r.max_concurrent_chats || 20}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-cream/30 border-b border-ink/10">
                                <tr>
                                    {["Recruiter", "Role", "Their status", "Visible", "Capacity", "In-flight", "Eligible"].map((h) => (
                                        <th key={h} className="px-4 py-2 text-left text-[10px] font-black uppercase tracking-wider text-ink/50">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {recs.map((r) => (
                                    <tr
                                        key={r.user_id}
                                        data-testid={`daddytalk-rec-row-${r.user_id}`}
                                        className="border-b border-ink/5"
                                    >
                                        <td className="px-4 py-2">
                                            <div className="flex flex-col">
                                                <span className="font-bold">{r.name || "Unnamed"}</span>
                                                <span className="text-[10px] text-ink/40">{r.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-ink/60 uppercase">
                                            {r.role === "hiring_manager" ? "Hiring Mgr" : r.role}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className={`text-[10px] font-black uppercase tracking-wider ${
                                                r.chat_active === "available" ? "text-emerald-600" :
                                                r.chat_active === "away" ? "text-amber-600" : "text-red-600"
                                            }`}>
                                                {r.chat_active}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            <button
                                                type="button"
                                                onClick={() => toggleVisibility(r.user_id, !r.chat_visible)}
                                                disabled={savingRow === r.user_id}
                                                data-testid={`daddytalk-visibility-toggle-${r.user_id}`}
                                                className={`relative inline-flex h-5 w-9 items-center transition-colors ${
                                                    r.chat_visible ? "bg-cobalt" : "bg-ink/20"
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform bg-white transition-transform ${
                                                        r.chat_visible ? "translate-x-4" : "translate-x-0.5"
                                                    }`}
                                                />
                                            </button>
                                        </td>
                                        <td className="px-4 py-2">
                                            <input
                                                type="number"
                                                min={1}
                                                max={200}
                                                defaultValue={r.max_concurrent_chats || 20}
                                                onBlur={(e) => {
                                                    const v = Number(e.target.value);
                                                    if (v !== r.max_concurrent_chats) updateCapacity(r.user_id, v);
                                                }}
                                                data-testid={`daddytalk-capacity-${r.user_id}`}
                                                className="w-16 px-2 py-1 border border-ink/20 text-xs focus:outline-none focus:border-cobalt"
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-xs">
                                            {r.current_open_chats || 0} / {r.max_concurrent_chats || 20}
                                        </td>
                                        <td className="px-4 py-2">
                                            {r.eligible ? (
                                                <span
                                                    data-testid={`daddytalk-eligible-yes-${r.user_id}`}
                                                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-600"
                                                >
                                                    <Check size={11} weight="bold" />
                                                    Live
                                                </span>
                                            ) : (
                                                <span
                                                    data-testid={`daddytalk-eligible-no-${r.user_id}`}
                                                    className="text-[10px] font-black uppercase tracking-wider text-ink/30"
                                                >
                                                    Hidden
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
            </section>

            {/* Presence Leaderboard (V1.6.3) */}
            <section
                className="bg-white border-2 border-ink/10"
                data-testid="daddytalk-presence-leaderboard"
            >
                <div className="px-6 py-4 border-b-2 border-ink/10 flex items-center gap-3">
                    <Clock size={18} weight="bold" className="text-cobalt" />
                    <div>
                        <h2 className="font-heading text-base font-bold">
                            Presence leaderboard
                        </h2>
                        <p className="text-xs text-ink/50 mt-0.5">
                            Live-time and session counts across the last 7 days. Green dot = currently online (heartbeat within 90 s).
                        </p>
                    </div>
                </div>
                {(!presence || (presence.items || []).length === 0) ? (
                    <div
                        data-testid="daddytalk-presence-empty"
                        className="p-8 text-center text-xs text-ink/50"
                    >
                        No presence data yet — recruiters must sign in and open the app to log a session.
                    </div>
                ) : (
                    <>
                    {/* Mobile cards */}
                    <div className="sm:hidden divide-y divide-ink/5">
                        {(presence.items || []).map((r) => (
                            <div key={`m-p-${r.user_id}`} data-testid={`daddytalk-presence-card-${r.user_id}`} className="p-4">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                        <p className="font-bold text-xs truncate">{r.name}</p>
                                        <p className="text-[10px] uppercase tracking-wider text-ink/40 mt-0.5">
                                            {r.role === "hiring_manager" ? "HM" : r.role}
                                        </p>
                                    </div>
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider shrink-0 ${r.is_online ? "text-emerald-600" : "text-amber-600"}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${r.is_online ? "bg-emerald-500" : "bg-amber-400"}`} />
                                        {r.is_online ? "Online" : "Away"}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-ink/5 text-xs">
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-ink/40">Sessions 7d</p>
                                        <p>{r.sessions}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-ink/40">Est. live</p>
                                        <p>~{r.est_live_minutes} min</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-ink/40">First online today</p>
                                        <p className="text-ink/60">
                                            {r.first_online_today
                                                ? new Date(r.first_online_today).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                : "—"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-wider text-ink/40">Last seen</p>
                                        <p className="text-ink/60">
                                            {r.last_seen_at
                                                ? new Date(r.last_seen_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                                                : "—"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-cream text-[10px] font-black uppercase tracking-wider text-ink/50">
                                <tr>
                                    <th className="px-4 py-2 text-left">Recruiter</th>
                                    <th className="px-4 py-2 text-left">Status</th>
                                    <th className="px-4 py-2 text-left">Sessions (7d)</th>
                                    <th className="px-4 py-2 text-left">Est. live time</th>
                                    <th className="px-4 py-2 text-left">First online today</th>
                                    <th className="px-4 py-2 text-left">Last seen</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                {(presence.items || []).map((r) => (
                                    <tr
                                        key={r.user_id}
                                        data-testid={`daddytalk-presence-row-${r.user_id}`}
                                        className="border-t border-ink/5"
                                    >
                                        <td className="px-4 py-2 font-bold text-xs">
                                            {r.name}
                                            <span className="ml-2 text-[10px] uppercase tracking-wider text-ink/40">
                                                {r.role === "hiring_manager" ? "HM" : r.role}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            <span
                                                className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${r.is_online ? "text-emerald-600" : "text-amber-600"}`}
                                            >
                                                <span
                                                    className={`w-1.5 h-1.5 rounded-full ${r.is_online ? "bg-emerald-500" : "bg-amber-400"}`}
                                                />
                                                {r.is_online ? "Online" : "Away"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-xs">{r.sessions}</td>
                                        <td className="px-4 py-2 text-xs">
                                            ~{r.est_live_minutes} min
                                        </td>
                                        <td className="px-4 py-2 text-xs text-ink/60">
                                            {r.first_online_today
                                                ? new Date(r.first_online_today).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-ink/60">
                                            {r.last_seen_at
                                                ? new Date(r.last_seen_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
            </section>

            {/* Helpful footer */}
            <div className="text-xs text-ink/50 flex items-center gap-2">
                <ChatCircleDots size={12} weight="bold" className="text-cobalt" />
                <span>
                    Recruiters can pause their own visibility from their DaddyTalk header toggle
                    (away / DND). This page is the master control — flip <em>Visible</em> off here
                    to override any self-toggle.
                </span>
            </div>
        </div>
    );
}

function ToggleRow({ label, hint, value, onChange, disabled, testid }) {
    return (
        <div className={`flex items-start justify-between gap-6 ${disabled ? "opacity-50" : ""}`}>
            <div className="min-w-0 flex-1">
                <p className="font-bold text-sm">{label}</p>
                <p className="text-xs text-ink/50 mt-0.5">{hint}</p>
            </div>
            <button
                type="button"
                onClick={() => !disabled && onChange(!value)}
                disabled={disabled}
                data-testid={testid}
                className={`relative inline-flex h-6 w-11 items-center transition-colors shrink-0 ${
                    value ? "bg-cobalt" : "bg-ink/20"
                } ${disabled ? "cursor-not-allowed" : ""}`}
            >
                <span
                    className={`inline-block h-5 w-5 transform bg-white transition-transform ${
                        value ? "translate-x-5" : "translate-x-0.5"
                    }`}
                />
            </button>
        </div>
    );
}
